/**
 * Statutory due dates for Indian compliance returns.
 * Maps subtask title → function(period) → due date string (YYYY-MM-DD) or null.
 * All dates are now dynamically computed from the period string itself.
 */

// Helper: get next month date
const nextMonthDate = (period, day) => {
  const [year, month] = period.split('-').map(Number);
  let ny = month === 12 ? year + 1 : year;
  let nm = month === 12 ? 1 : month + 1;
  return `${ny}-${String(nm).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

// Helper: extract FY start year from a period string
// "2025-Q1" → 2025, "2026-04" → 2026, "FY2025-26" → 2025
const fyStartFromPeriod = (period) => {
  if (period.startsWith('FY')) return parseInt(period.replace('FY', '').split('-')[0]);
  return parseInt(period.split('-')[0]);
};

// Helper: quarterly due date based on period year
const quarterlyDue = (period, q1Day, q2Day, q3Day, q4Day, q4Month = 4) => {
  const fyStart = fyStartFromPeriod(period);
  const q = period.split('-')[1];
  if (q === 'Q1') return `${fyStart}-07-${String(q1Day).padStart(2, '0')}`;
  if (q === 'Q2') return `${fyStart}-10-${String(q2Day).padStart(2, '0')}`;
  if (q === 'Q3') return `${fyStart + 1}-01-${String(q3Day).padStart(2, '0')}`;
  if (q === 'Q4') return `${fyStart + 1}-${String(q4Month).padStart(2, '0')}-${String(q4Day).padStart(2, '0')}`;
  return null;
};

/**
 * GST Due Dates (Monthly)
 */
const MONTHLY_DUE_DATES = {
  'GSTR-3B': (period) => nextMonthDate(period, 20),
  'GSTR-1': (period) => nextMonthDate(period, 11),
  'GSTR-6': (period) => nextMonthDate(period, 13),
  'GSTR-7': (period) => nextMonthDate(period, 10),
  'TDS Payment': (period) => {
    const [year, month] = period.split('-').map(Number);
    if (month === 3) return `${year}-04-30`;
    const ny = month === 12 ? year + 1 : year;
    const nm = month === 12 ? 1 : month + 1;
    return `${ny}-${String(nm).padStart(2, '0')}-07`;
  },
};

/**
 * Quarterly Due Dates
 */
const QUARTERLY_DUE_DATES = {
  'CMP-08': (period) => {
    const fyStart = fyStartFromPeriod(period);
    const q = period.split('-')[1];
    if (q === 'Q1') return `${fyStart}-07-18`;
    if (q === 'Q2') return `${fyStart}-10-18`;
    if (q === 'Q3') return `${fyStart + 1}-01-18`;
    if (q === 'Q4') return `${fyStart + 1}-04-18`;
    return null;
  },
  'Form 24Q': (period) => quarterlyDue(period, 31, 31, 31, 31, 5),
  '24Q': (period) => quarterlyDue(period, 31, 31, 31, 31, 5),
  'Form 26Q': (period) => quarterlyDue(period, 31, 31, 31, 31, 5),
  '26Q': (period) => quarterlyDue(period, 31, 31, 31, 31, 5),
  'Form 27Q': (period) => quarterlyDue(period, 31, 31, 31, 31, 5),
  '27Q': (period) => quarterlyDue(period, 31, 31, 31, 31, 5),
  'Form 27EQ': (period) => quarterlyDue(period, 15, 15, 15, 15, 5),
  '27EQ': (period) => quarterlyDue(period, 15, 15, 15, 15, 5),
  'Form 24Q (Salary TDS)': (period) => quarterlyDue(period, 31, 31, 31, 31, 5),
  'Form 26Q (Non-Salary TDS)': (period) => quarterlyDue(period, 31, 31, 31, 31, 5),
  'Form 27Q (NRI TDS)': (period) => quarterlyDue(period, 31, 31, 31, 31, 5),
  'Form 27EQ (TCS)': (period) => quarterlyDue(period, 15, 15, 15, 15, 5),
  'Advance Tax - Q1': (period) => { const y = fyStartFromPeriod(period); return `${y}-06-15`; },
  'Advance Tax - Q2': (period) => { const y = fyStartFromPeriod(period); return `${y}-09-15`; },
  'Advance Tax - Q3': (period) => { const y = fyStartFromPeriod(period); return `${y}-12-15`; },
  'Advance Tax - Q4': (period) => { const y = fyStartFromPeriod(period); return `${y + 1}-03-15`; },
};

/**
 * Half-Yearly Due Dates
 */
const HALF_YEARLY_DUE_DATES = {
  'MSME Form': (period) => {
    const fyStart = fyStartFromPeriod(period);
    const h = period.split('-')[1];
    if (h === 'H1') return `${fyStart}-10-30`;  // Apr-Sep → 30 Oct
    if (h === 'H2') return `${fyStart + 1}-04-30`; // Oct-Mar → 30 Apr
    return null;
  },
};

/**
 * Annual Due Dates
 */
const ANNUAL_DUE_DATES = {
  'GSTR-4': (period) => { const y = fyStartFromPeriod(period); return `${y + 1}-04-30`; },
  'GSTR-9': (period) => { const y = fyStartFromPeriod(period); return `${y + 1}-12-31`; },
  'GSTR-9C': (period) => { const y = fyStartFromPeriod(period); return `${y + 1}-12-31`; },
  'ITR Filing': (period) => { const y = fyStartFromPeriod(period); return `${y + 1}-07-31`; },
  'ITR': (period) => { const y = fyStartFromPeriod(period); return `${y + 1}-07-31`; },
  'Tax Audit (44AB)': (period) => { const y = fyStartFromPeriod(period); return `${y + 1}-09-30`; },
  'Statutory Audit': (period) => { const y = fyStartFromPeriod(period); return `${y + 1}-09-30`; },
  'Tax Audit': (period) => { const y = fyStartFromPeriod(period); return `${y + 1}-09-30`; },
  'GST Audit': (period) => { const y = fyStartFromPeriod(period); return `${y + 1}-12-31`; },
  'AOC-4 (Financials)': (period) => { const y = fyStartFromPeriod(period); return `${y + 1}-10-29`; },
  'AOC-4': (period) => { const y = fyStartFromPeriod(period); return `${y + 1}-10-29`; },
  'MGT-7 (Annual Return)': (period) => { const y = fyStartFromPeriod(period); return `${y + 1}-11-28`; },
  'MGT-7A': (period) => { const y = fyStartFromPeriod(period); return `${y + 1}-11-28`; },
  'DIR-3 KYC': (period) => { const y = fyStartFromPeriod(period); return `${y + 1}-09-30`; },
  'DPT-3': (period) => { const y = fyStartFromPeriod(period); return `${y + 1}-06-30`; },
  'Form 11': (period) => { const y = fyStartFromPeriod(period); return `${y + 1}-05-30`; },
  'Form 8': (period) => { const y = fyStartFromPeriod(period); return `${y + 1}-10-30`; },
  'Trust Audit': (period) => { const y = fyStartFromPeriod(period); return `${y + 1}-10-31`; },
};

/**
 * Get due date for a subtask + period combination.
 * @param {string} subtaskTitle - e.g., "GSTR-3B", "Form 24Q"
 * @param {string} period - e.g., "2026-04", "2025-Q1", "FY2025-26"
 * @param {string} frequency - "monthly" | "quarterly" | "annual"
 * @param {object} dueDateRule - Optional DB-stored rule for fallback
 * @returns {string|null} - "YYYY-MM-DD" or null
 */
export const getDueDate = (subtaskTitle, period, frequency, dueDateRule) => {
  if (frequency === 'monthly' && MONTHLY_DUE_DATES[subtaskTitle]) {
    return MONTHLY_DUE_DATES[subtaskTitle](period);
  }
  if (frequency === 'quarterly' && QUARTERLY_DUE_DATES[subtaskTitle]) {
    return QUARTERLY_DUE_DATES[subtaskTitle](period);
  }
  if (frequency === 'half_yearly' && HALF_YEARLY_DUE_DATES[subtaskTitle]) {
    return HALF_YEARLY_DUE_DATES[subtaskTitle](period);
  }
  if ((frequency === 'annual' || frequency === 'annually' || frequency === '3_yearly') && ANNUAL_DUE_DATES[subtaskTitle]) {
    return ANNUAL_DUE_DATES[subtaskTitle](period);
  }
  // Fallback: apply DB-stored due_date_rule
  if (dueDateRule && Object.keys(dueDateRule).length > 0) {
    return applyDueDateRule(dueDateRule, period, frequency);
  }
  return null;
};

/**
 * Apply a DB-stored due_date_rule to compute due date on frontend.
 */
const applyDueDateRule = (rule, period, frequency) => {
  try {
    if (frequency === 'monthly') {
      const [year, month] = period.split('-').map(Number);
      const exc = rule.exceptions || {};
      if (exc[String(month)]) {
        const em = parseInt(exc[String(month)].month);
        const ed = parseInt(exc[String(month)].day);
        const ey = em < month ? year + 1 : year;
        return `${ey}-${String(em).padStart(2, '0')}-${String(ed).padStart(2, '0')}`;
      }
      const day = parseInt(rule.day) || 1;
      if (rule.next_month !== false) {
        const ny = month === 12 ? year + 1 : year;
        const nm = month === 12 ? 1 : month + 1;
        return `${ny}-${String(nm).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      }
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
    if (frequency === 'quarterly') {
      const q = period.split('-')[1];
      const qr = rule[q];
      if (qr) {
        const fyStart = parseInt(period.split('-')[0]);
        const rm = parseInt(qr.month);
        const rd = parseInt(qr.day);
        const ry = rm <= 3 ? fyStart + 1 : fyStart;
        return `${ry}-${String(rm).padStart(2, '0')}-${String(rd).padStart(2, '0')}`;
      }
    }
    if (frequency === 'half_yearly') {
      const h = period.split('-')[1];
      const hr = rule[h];
      if (hr) {
        const fyStart = parseInt(period.split('-')[0]);
        const rm = parseInt(hr.month);
        const rd = parseInt(hr.day);
        const ry = rm <= 3 ? fyStart + 1 : fyStart;
        return `${ry}-${String(rm).padStart(2, '0')}-${String(rd).padStart(2, '0')}`;
      }
    }
    if (frequency === 'annual' || frequency === 'annually' || frequency === '3_yearly') {
      const fyStart = fyStartFromPeriod(period);
      const rm = parseInt(rule.month) || 7;
      const rd = parseInt(rule.day) || 31;
      return `${fyStart + 1}-${String(rm).padStart(2, '0')}-${String(rd).padStart(2, '0')}`;
    }
  } catch (e) { 
    if (process.env.NODE_ENV !== 'production') console.warn('Due date rule parse error:', e.message);
  }
  return null;
};

/**
 * Check if a due date is overdue (past today and not filed).
 * @param {string} dueDateStr - "YYYY-MM-DD"
 * @returns {boolean}
 */
export const isOverdue = (dueDateStr) => {
  if (!dueDateStr) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDateStr + 'T00:00:00');
  return due < today;
};

/**
 * Check if due date is within 7 days.
 */
export const isDueSoon = (dueDateStr) => {
  if (!dueDateStr) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDateStr + 'T00:00:00');
  const diff = (due - today) / (1000 * 60 * 60 * 24);
  return diff >= 0 && diff <= 7;
};

/**
 * Format a due date string to short display format.
 * "2026-05-20" → "20 May"
 */
export const formatDueDate = (dueDateStr) => {
  if (!dueDateStr) return '';
  const d = new Date(dueDateStr + 'T00:00:00');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${d.getDate()} ${months[d.getMonth()]}`;
};
